import useSWR from 'swr';
import { APPROVAL_V2_URL } from '@/constant/approval';
import { getNestedDataFetcher } from '@/utils/apis';
import type { ApprovalViewResponse } from '@/components/Approval/ApprovalView/types';

/**
 * 품의 단건 조회 (조회 화면용).
 * - GET /api/v2/approval/{documentId}
 * - 1번 유형(`PORTAL-10726`) 조회 화면의 페이지 레벨 fetch.
 * - documentId 없으면 호출 skip (SWR key null).
 *
 * 작성용 `hooks/approval/useApproval.ts` 와 같은 엔드포인트지만 응답 필드 범위가 다르다
 * (조회는 author/approvers/referrers/attachments 등을 포함한 풀 응답, 편집 모드 진입은
 * 폼 복원에 필요한 부분 응답). 추후 응답 통합 시 합치는 건 별 작업.
 *
 * SWR 옵션:
 * - `keepPreviousData: true` — step E 의 결재 액션 후 mutate 로 무효화될 때 잠깐 빈 화면
 *   대신 직전 응답을 유지하기 위함.
 * - `revalidateOnFocus: false` — 조회 화면 특성상 포커스 복귀마다 재조회는 과함.
 *   사용자 액션(승인/반려) 후 명시적 mutate 만 의존.
 */
const useApprovalView = (documentId?: string | null) => {
  // 작성용 `useApproval` 과 endpoint 가 같아 key 가 같으면 SWR 캐시가 공유되어 shape 다른
  // 두 응답(ApprovalDetailResponse/ApprovalViewResponse)이 충돌 → tuple key 로 캐시 분리.
  // fetcher 는 URL 요소만 사용 — query param 추가 없음.
  const { data, error, isLoading, mutate } = useSWR<ApprovalViewResponse>(
    documentId
      ? ([`${APPROVAL_V2_URL}/${documentId}`, 'view'] as const)
      : null,
    ([url]) => getNestedDataFetcher<ApprovalViewResponse>(url),
    {
      keepPreviousData: true,
      revalidateOnFocus: false,
    },
  );

  return { data, error, isLoading, mutate };
};

export default useApprovalView;
